import React from 'react';
import { motion } from 'framer-motion';

const HeroSection = () => {
  return (
    <div className="bg-gray-50 px-6 py-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Content */}
          <motion.div 
            className="space-y-12"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Quote Section */}
            <div className="space-y-4">
              <p className="text-gray-600 text-base font-medium">Inspiration</p>
              <motion.div 
                className="relative"
                initial={{ 
                  opacity: 0, 
                  y: 50,
                  scale: 0.5,
                  rotateX: -90 
                }}
                animate={{ 
                  opacity: 1, 
                  y: 0,
                  scale: 1,
                  rotateX: 0 
                }}
                transition={{ 
                  delay: 0.2, 
                  duration: 1.2,
                  type: "spring",
                  stiffness: 120,
                  damping: 12
                }}
                whileHover={{
                  scale: 1.02,
                  transition: { duration: 0.3 }
                }}
              >
                <motion.blockquote 
                  className="text-4xl lg:text-5xl font-bold text-orange-500 leading-tight relative italic"
                  initial={{ opacity: 0.8, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.1 }}
                >
                  "Every great event begins with a vision and ends with unforgettable memories."
                </motion.blockquote>
                
                {/* Enhanced floating particles effect */}
                <motion.div
                  className="absolute -top-2 -right-2 w-3 h-3 bg-orange-400 rounded-full"
                  animate={{
                    y: [-5, -15, -5],
                    opacity: [0.6, 1, 0.6],
                    scale: [0.8, 1.2, 0.8]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                <motion.div
                  className="absolute -top-4 left-4 w-2 h-2 bg-orange-300 rounded-full"
                  animate={{
                    y: [-8, -18, -8],
                    opacity: [0.4, 0.8, 0.4],
                    scale: [0.6, 1, 0.6]
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.5
                  }}
                />
                <motion.div
                  className="absolute -bottom-2 left-8 w-2 h-2 bg-orange-200 rounded-full"
                  animate={{
                    y: [5, 15, 5],
                    opacity: [0.5, 0.9, 0.5],
                    scale: [0.7, 1.1, 0.7]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                  }}
                />
                
                {/* Inspiration glow effect */}
                <motion.div
                  className="absolute inset-0 bg-orange-500 opacity-10 rounded-lg blur-lg"
                  animate={{
                    scale: [1, 1.02, 1],
                    opacity: [0.05, 0.15, 0.05]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </motion.div>
              <p className="text-gray-600 text-base leading-relaxed max-w-sm">
                Explore a wide range of venues at your fingertips. From luxury banquet halls to cozy outdoor gardens, our app gives you access to thousands of verified venues to fit every occasion and budget.
              </p>
            </div>

            {/* Welcome Section */}
            <div className="space-y-6">
              <p className="text-gray-600 text-base font-medium">Welcome To Eventplanly.</p>
              
              <motion.h3 
                className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight max-w-md"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                Download the EventPlanly app on your device & Book the Vendor Services Just by Paying{' '}
                <span className="text-orange-500">30% Token Amount.</span>
              </motion.h3>
            </div>
          </motion.div>

          {/* Right Content - Hero Image with Badge */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Main Heading positioned above image */}
            <motion.div 
              className="mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Find your perfect<br />
                Event Venue
              </h1>
            </motion.div>

            {/* Image Container with Badge */}
            <div className="relative">
              {/* Event Planly Badge */}
              <motion.div 
                className="absolute -top-8 -right-8 z-10 w-20 h-20"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ 
                  type: "spring",
                  stiffness: 260,
                  damping: 20,
                  delay: 0.8 
                }}
                whileHover={{ 
                  scale: 1.1,
                  rotate: 10,
                  transition: { duration: 0.2 }
                }}
              >
                <img 
                  src="/images/EVP-logo.png" 
                  alt="Event Planly Badge"
                  className="w-full h-full object-contain drop-shadow-lg"
                />
              </motion.div>

              {/* Main Event Image */}
              <motion.div 
                className="rounded-3xl overflow-hidden shadow-2xl aspect-[4/3] w-full max-w-lg"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src="/images/wedding-venue.png" 
                  alt="Luxury wedding venue with chandelier and elegant stage decoration"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;